import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync, useDebounced, useErrorMessage } from '../lib/hooks';
import { useI18n, useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { useToast } from '../components/Toast';
import { Icon } from '../components/Icon';
import { ProductForm } from '../components/ProductForm';
import {
  ActiveBadge, ConfirmDialog, EmptyState, ErrorAlert, LoadingRow, Modal, Pagination, SearchInput,
} from '../components/ui';
import { formatDate, formatMoney } from '@shared/format';
import type { Product } from '@shared/types';

const PER_PAGE = 20;

export function Products() {
  const t = useT();
  const { locale } = useI18n();
  const { money } = useSession();
  const toast = useToast();
  const toMessage = useErrorMessage();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Product | 'new'>();
  const [pendingDelete, setPendingDelete] = useState<Product>();
  const [deleting, setDeleting] = useState(false);

  const q = useDebounced(search);
  const { data, loading, error, reload } = useAsync(
    () => api.products({ q, page, perPage: PER_PAGE }), [q, page]);

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.deleteProduct(pendingDelete.id);
      toast.success(t('common.deleted'));
      setPendingDelete(undefined);
      reload();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div><h1 className="page-head__title">{t('products.title')}</h1></div>
        <div className="page-head__actions">
          <button type="button" className="btn btn--primary" onClick={() => setEditing('new')}>
            <Icon name="plus" />{t('products.new')}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card__head">
          <div className="toolbar" style={{ flex: 1 }}>
            <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }}
              placeholder={t('products.searchPlaceholder')} />
            {loading && data && <span className="spinner" />}
          </div>
        </div>

        {error ? <div className="card__body"><ErrorAlert message={error} onRetry={reload} /></div>
          : loading && !data ? <LoadingRow />
          : data && data.items.length === 0 ? (
            <EmptyState
              icon={q ? 'search' : 'box'}
              title={q ? t('common.noResults') : t('products.empty')}
              hint={q ? t('common.noResultsHint') : t('products.emptyHint')}
              action={!q && (
                <button type="button" className="btn btn--primary" onClick={() => setEditing('new')}>
                  <Icon name="plus" />{t('products.new')}
                </button>
              )}
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th className="shrink">{t('products.field.code')}</th>
                    <th>{t('products.field.name')}</th>
                    <th className="num">{t('products.field.unitPrice')}</th>
                    <th className="shrink">{t('common.status')}</th>
                    <th className="shrink">{t('common.created')}</th>
                    <th className="shrink" />
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((product) => (
                    <tr key={product.id}>
                      <td className="shrink mono">{product.code}</td>
                      <td><span className="table__primary">{product.name}</span></td>
                      <td className="num table__primary">{formatMoney(product.unitPrice, money)}</td>
                      <td className="shrink"><ActiveBadge active={product.isActive} /></td>
                      <td className="shrink table__meta nowrap">{formatDate(product.createdAt, locale)}</td>
                      <td className="shrink">
                        <div className="table__actions">
                          <button type="button" className="btn btn--ghost btn--icon"
                            title={t('common.edit')} aria-label={t('common.edit')}
                            onClick={() => setEditing(product)}>
                            <Icon name="edit" />
                          </button>
                          <button type="button" className="btn btn--ghost btn--icon"
                            title={t('common.delete')} aria-label={t('common.delete')}
                            onClick={() => setPendingDelete(product)}>
                            <Icon name="trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        {data && data.items.length > 0 && (
          <div className="card__foot">
            <Pagination page={data.page} totalPages={data.totalPages} total={data.total}
              perPage={data.perPage} onChange={setPage} />
          </div>
        )}
      </div>

      {editing && (
        <Modal
          title={editing === 'new' ? t('products.new') : t('products.edit')}
          onClose={() => setEditing(undefined)}
        >
          <ProductForm
            product={editing === 'new' ? undefined : editing}
            onCancel={() => setEditing(undefined)}
            onSaved={(_, created) => {
              toast.success(t(created ? 'products.created' : 'products.updated'));
              setEditing(undefined);
              reload();
            }}
          />
        </Modal>
      )}

      {pendingDelete && (
        <ConfirmDialog
          title={t('common.delete')}
          message={`${t('products.deleteConfirm', { name: pendingDelete.name })}\n\n${t('products.deleteKeepsHistory')}`}
          confirmLabel={t('common.delete')}
          danger
          busy={deleting}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setPendingDelete(undefined)}
        />
      )}
    </>
  );
}
